import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useLenis } from "../hooks/useLenis";
import { useSeo } from "../hooks/useSeo";
import { SubNav } from "./SubNav";
import { Footer } from "./Footer";
import { WhatsAppFloat } from "./WhatsAppFloat";
import { Reveal } from "../lib/motion";

export const ContentPage = ({ eyebrow, title, lead, children, seo, testid = "content-page" }) => {
  useLenis();
  useSeo(seo);

  return (
    <div className="bg-graphite text-white min-h-screen" data-testid={testid}>
      <SubNav />
      <main>
        {/* Hero header */}
        <header className="relative pt-36 pb-16 md:pt-44 md:pb-20 overflow-hidden border-b border-white/10">
          <div className="pointer-events-none absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full blur-[120px] bg-coral/10" />
          <div className="container-pm relative">
            <motion.p
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              className="eyebrow mb-4"
            >
              {eyebrow}
            </motion.p>
            <motion.h1
              initial={{ opacity: 0, y: 24 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="font-display font-semibold text-white text-[clamp(34px,5.4vw,68px)] leading-[1.02] tracking-[-0.04em] max-w-[20ch]"
            >
              {title}
            </motion.h1>
            {lead && (
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.15, duration: 0.7 }}
                className="mt-6 text-white/70 text-base sm:text-lg max-w-[56ch]"
              >
                {lead}
              </motion.p>
            )}
          </div>
        </header>

        {/* Body */}
        <div className="container-pm py-[clamp(48px,7vw,96px)]">{children}</div>

        {/* CTA band */}
        <section className="relative border-t border-white/10 bg-surface/40">
          <div className="container-pm py-16 md:py-20 text-center">
            <Reveal>
              <h2 className="font-display font-semibold text-white text-[clamp(28px,3.6vw,46px)] leading-tight tracking-[-0.03em]">
                Gotowa na <span className="text-coral">wielkie pieniądze?</span>
              </h2>
              <p className="mt-4 text-white/65 max-w-[46ch] mx-auto">
                Wypełnij formularz — odpowiadamy dyskretnie w 24h, bez zobowiązań. Współpraca 18+.
              </p>
              <a
                href="/#aplikuj"
                data-testid="content-cta"
                className="group mt-8 inline-flex items-center gap-2 h-[52px] px-7 rounded-lg bg-coral text-white font-bold hover:bg-coral-hover hover:-translate-y-0.5 transition-all"
              >
                Sprawdź model współpracy
                <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
              </a>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
};

// Reusable content building blocks
export const Section = ({ title, children, className = "" }) => (
  <Reveal className={`mb-14 ${className}`}>
    {title && (
      <h2 className="font-display font-semibold text-white text-[clamp(24px,2.8vw,36px)] leading-tight tracking-[-0.03em] mb-6">
        {title}
      </h2>
    )}
    {children}
  </Reveal>
);

export const FeatureCard = ({ icon: Icon, title, text }) => (
  <div className="pm-card p-7 h-full">
    {Icon && (
      <div className="mb-5 flex items-center justify-center w-11 h-11 rounded-lg bg-coral/15 text-coral">
        <Icon size={22} strokeWidth={1.7} />
      </div>
    )}
    <h3 className="font-display font-semibold text-lg text-white mb-2">{title}</h3>
    <p className="text-sm text-white/65 leading-relaxed">{text}</p>
  </div>
);

export const Prose = ({ children }) => (
  <div className="max-w-[70ch] space-y-5 text-white/70 leading-relaxed [&_h2]:font-display [&_h2]:font-semibold [&_h2]:text-white [&_h2]:text-xl [&_h2]:mt-10 [&_h2]:mb-3 [&_a]:text-coral [&_a]:underline [&_strong]:text-white [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:space-y-2">
    {children}
  </div>
);
